import { useCallback, useMemo } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import { useTranslate } from '@i18n';
import { useUiParameters } from '@queries';
import { Box, Button, CloseIcon, FilterIcon, Inline, Pill, Text, u } from '@ui';

import * as css from './ParameterFilter.css';
import {
    FilterGroupKey,
    FilterKey,
    FilterSelectionKey,
    parameterFilterGroupEntries,
    parameterFilterGroups,
    parameterFilterLabels,
    parameterFilters,
} from './parameterFilters';

import type { ParameterFilterFormValues } from './ParameterFilterProvider';

const isFilterGroupKey = (filterKey: FilterSelectionKey): filterKey is FilterGroupKey =>
    filterKey in parameterFilterGroups;

const getParentFilterKey = (filterKey: FilterKey): FilterSelectionKey =>
    parameterFilterGroupEntries.find(([, group]) => group.children.some((child) => child === filterKey))?.[0] ??
    filterKey;

const getChildFilterKeys = (filterKey: FilterGroupKey) => parameterFilterGroups[filterKey].children;

const updateFilterSelection = (filterValues: readonly FilterSelectionKey[], filterKey: FilterSelectionKey) => {
    const keysToClear = isFilterGroupKey(filterKey)
        ? [filterKey, ...getChildFilterKeys(filterKey)]
        : [getParentFilterKey(filterKey), filterKey];
    const filtersWithoutSelection = filterValues.filter((key) => !keysToClear.includes(key));

    return filterValues.includes(filterKey) ? filtersWithoutSelection : [...filtersWithoutSelection, filterKey];
};

/**
 * Renders the group, parameter, and position filter controls.
 *
 * @returns The parameter filter controls.
 */
export const ParameterFilter = () => {
    const { control, setValue } = useFormContext<ParameterFilterFormValues>();
    const filterValues = useWatch({ control, name: 'filters' });
    const positionValues = useWatch({ control, name: 'positions' });
    const filters = useMemo(() => new Set(filterValues), [filterValues]);

    const toggleFilter = useCallback(
        (filterKey: FilterSelectionKey) => {
            setValue('filters', updateFilterSelection(filterValues, filterKey), { shouldDirty: true });
        },
        [filterValues, setValue],
    );

    const togglePosition = useCallback(
        (position: string) => {
            const nextPositions = positionValues.includes(position)
                ? positionValues.filter((currentPosition) => currentPosition !== position)
                : [...positionValues, position];

            setValue('positions', nextPositions, { shouldDirty: true });
        },
        [positionValues, setValue],
    );

    const clearFilters = useCallback(() => {
        setValue('filters', [], { shouldDirty: true });
        setValue('positions', [], { shouldDirty: true });
    }, [setValue]);

    const t = useTranslate();
    const { data: parameters = [] } = useUiParameters();
    const availablePositions = useMemo(
        () =>
            [
                ...new Set(
                    parameters
                        .filter(({ categories }) => categories.swingFoundationGroup)
                        .map(({ parameter }) => parameter.position)
                        .filter((position) => position !== null),
                ),
            ].sort(),
        [parameters],
    );
    return (
        <Box display="flex" flexDirection="column" gap={u(1)} paddingBlockStart={u(1)}>
            <Box className={css.row}>
                <FilterIcon />
                <Text variant="small" whiteSpace="nowrap" textAlign="right">
                    {t('parameter_selection.group_label')}
                </Text>
                <Inline alignItems="center" flexWrap="wrap" gap={u(1.5)}>
                    {parameterFilterGroupEntries.map(([filterKey, group]) => (
                        <Pill key={filterKey} active={filters.has(filterKey)} onClick={() => toggleFilter(filterKey)}>
                            <Text variant="small">{t(group.label)}</Text>
                            {filters.has(filterKey) && <CloseIcon width={u(1.5)} height={u(1.5)} />}
                        </Pill>
                    ))}
                </Inline>
            </Box>
            <Box className={css.row}>
                <span />
                <Text variant="small" whiteSpace="nowrap" textAlign="right">
                    {t('parameter_selection.parameters')}
                </Text>
                <Inline alignItems="center" flexWrap="wrap" gap={u(1.5)}>
                    {parameterFilters.map((filterKey) => (
                        <Pill key={filterKey} active={filters.has(filterKey)} onClick={() => toggleFilter(filterKey)}>
                            <Text variant="small">{t(parameterFilterLabels[filterKey])}</Text>
                            {filters.has(filterKey) && <CloseIcon width={u(1.5)} height={u(1.5)} />}
                        </Pill>
                    ))}
                </Inline>
            </Box>
            <Box className={css.row}>
                <span />
                <Text variant="small" whiteSpace="nowrap" textAlign="right">
                    {t('parameter_selection.positions')}
                </Text>
                <Inline alignItems="center" flexWrap="wrap" gap={u(1.5)}>
                    {availablePositions.map((position) => {
                        const isActive = positionValues.includes(position);

                        return (
                            <Pill key={position} active={isActive} onClick={() => togglePosition(position)}>
                                <Text variant="small">{position.toUpperCase()}</Text>
                                {isActive && <CloseIcon width={u(1.5)} height={u(1.5)} />}
                            </Pill>
                        );
                    })}
                    {(filters.size > 0 || positionValues.length > 0) && (
                        <Button size="tiny" variant="secondary" icon="close" onClick={clearFilters}>
                            {t('parameter_selection.clear_filters')}
                        </Button>
                    )}
                </Inline>
            </Box>
        </Box>
    );
};
